/**
 * ContentLinksWidget
 * 
 * Displays content metadata badges and links.
 * Matches Flutter's ContentLinksWidget from content_widget.dart
 * 
 * Features:
 * - Creation date
 * - View count
 * - Schedule indicator
 * - Early access badge
 * - Premium badge
 * - 4K badge
 * - Share link
 * - NSFW indicator
 * - Content link
 * - Comment reply indicator
 * - Draft status
 * - Blocked content indicator
 */

import React from 'react';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { MathUtils } from '@/domain/utils/mathUtils';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { Chip } from '@/presentation/components/common/Chip';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useTheme } from '@/presentation/contexts/ThemeContext';

export interface ContentLinksComponentProps {
  /** Media entity */
  media: UserMediaEntity;

  /** Whether to show share button (for own content) */
  showShare?: boolean;

  /** Whether content is NSFW */
  isNsfw?: boolean;

  /** Whether video is premium (long duration) */
  isPremiumVideo?: boolean;

  /** Whether video is 4K */
  is4k?: boolean;

  /** Minified mode (show only icons, no labels) */
  minified?: boolean;

  /** Current user ID */
  currentUserId?: string;

  /** Whether user has 4K access */
  has4kAccess?: boolean;

  /** Callback when content link is tapped */
  onContentLinkTap?: () => Promise<void> | void;

  /** Callback when share is tapped */
  onShareTap?: () => Promise<void> | void;

  /** Callback when comment reply is tapped */
  onCommentReplyTap?: () => Promise<void> | void;

  /** Callback when draft is tapped */
  onDraftTap?: () => Promise<void> | void;

  /** Callback when schedule is tapped */
  onScheduleTap?: () => Promise<void> | void;

  /** Callback when content blocked is tapped */
  onContentBlockedTap?: () => Promise<void> | void;

  onUnlockContentTap?: () => Promise<void> | void;

  /** Whether the current user has already unlocked this specific content */
  hasUnlockedContent?: boolean;

  /** Whether the current user has full exclusive rights (unlocked or subscribed) */
  hasExclusiveRights?: boolean;

  /** Callback when NSFW is tapped — receives the chip's media so the
      handler can branch on whether the viewer owns the content. */
  onNsfwTap?: (media: UserMediaEntity) => Promise<void> | void;

  /** Callback when Get 4K access is tapped */
  onGet4kAccessTap?: () => Promise<void> | void;

  /** Custom CSS class */
  className?: string;
}

export const ContentLinksComponent: React.FC<ContentLinksComponentProps> = ({
  media,
  showShare = false,
  isNsfw = false,
  isPremiumVideo = false,
  is4k = false,
  minified = false,
  currentUserId,
  has4kAccess = false,
  onContentLinkTap,
  onShareTap,
  onCommentReplyTap,
  onDraftTap,
  onScheduleTap,
  onContentBlockedTap,
  onUnlockContentTap,
  hasUnlockedContent = false,
  hasExclusiveRights = false,
  onNsfwTap,
  onGet4kAccessTap,
  className = '',
}) => {
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const isOwnUser = media.firebaseId === currentUserId;
  // Suffix every per-card link `data-qa-id` with the content id so the ids
  // stay page-unique across multiple cards (matches `content-item-<id>` etc.).
  const contentId = media.id;
  const chips: React.ReactNode[] = [];

  // Unlock charge badge
  if (media.allowUnlock && media.unlockCharge && media.unlockCharge > 0) {
    const isUnlocked = hasUnlockedContent || hasExclusiveRights;
    chips.push(
      <Chip
        key="unlock-charge"
        qa={{ id: `content-link-unlock-charge-${contentId}`, status: isUnlocked ? 'unlocked' : 'locked' }}
        label={minified ? undefined : `${MathUtils.formatCount(media.unlockCharge)}¢`}
        iconPath={isUnlocked ? '/assets/vectors/unlock-filled.svg' : '/assets/vectors/lock-filled.svg'}
        iconColor={isUnlocked ? theme.icon.primary : theme.icon.secondary}
        onClick={onUnlockContentTap}
      />
    );
  }

  // Date
  if (!minified) {
    chips.push(
      <Chip
        key="date"
        qa={{ id: `content-link-date-${contentId}` }}
        label={dateFormatters.formatDate(media.createdTimestamp)}
      />
    );
  }

  // Views
  if (!minified) {
    chips.push(
      <Chip
        key="views"
        qa={{ id: `content-link-views-${contentId}`, value: media.views || 0 }}
        label={`${MathUtils.formatCount(media.views || 0)} ${l10n.content.views}`}
      />
    );
  }

  // Schedule (own content only)
  if (isOwnUser && media.schedule) {
    chips.push(
      <Chip
        key="schedule"
        qa={{ id: `content-link-schedule-${contentId}` }}
        label={minified ? undefined : `${l10n.content.scheduled} - ${dateFormatters.formatDate(media.schedule, true)}`}
        iconPath={minified ? '/assets/images/timer.png' : undefined}
        onClick={onScheduleTap}
      />
    );
  }

  // Early Access (not own content)
  if (!isOwnUser && media.allowEarlyAccess) {
    chips.push(
      <Chip
        key="early-access"
        qa={{ id: `content-link-early-access-${contentId}` }}
        label={minified ? undefined : l10n.content.earlyAccess}
        iconPath={minified ? '/assets/images/early_access.png' : undefined}
      />
    );
  }

  // Premium
  if (isPremiumVideo) {
    chips.push(
      <Chip
        key="premium"
        qa={{ id: `content-link-premium-${contentId}` }}
        label={minified ? undefined : l10n.content.premium}
        iconPath={minified ? '/assets/images/premium_content.png' : undefined}
      />
    );
  }

  // 4K
  if (is4k) {
    chips.push(
      <Chip
        key="4k"
        qa={{ id: `content-link-4k-${contentId}`, status: has4kAccess ? 'unlocked' : 'locked' }}
        label={minified || has4kAccess ? l10n.content.fourK : l10n.content.unlockFourK}
        onClick={minified || has4kAccess ? undefined : onGet4kAccessTap}
      />
    );
  }

  // Share (own content only)
  if (!minified && showShare) {
    chips.push(
      <Chip
        key="share"
        qa={{ id: `content-link-share-${contentId}` }}
        label={l10n.content.share}
        onClick={onShareTap}
      />
    );
  }

  // NSFW
  if (isNsfw) {
    chips.push(
      <Chip
        key="nsfw"
        qa={{ id: `content-link-nsfw-${contentId}` }}
        label={l10n.content.nsfw}
        onClick={onNsfwTap ? () => onNsfwTap(media) : undefined}
      />
    );
  }

  // Content Link
  if (media.contentLink) {
    chips.push(
      <Chip
        key="content-link"
        qa={{ id: `content-link-link-${contentId}` }}
        label={minified ? undefined : l10n.content.link}
        iconPath={minified ? '/assets/images/link.png' : undefined}
        onClick={onContentLinkTap}
      />
    );
  }

  // Comment Reply
  if (!minified && media.contentReplyCommentInfo) {
    chips.push(
      <Chip
        key="comment-reply"
        qa={{ id: `content-link-comment-reply-${contentId}` }}
        label={l10n.content.repliedTo.replace('{name}', media.contentReplyCommentInfo.replyUsername)}
        iconPath={'/assets/vectors/comment_fill.svg'}
        onClick={onCommentReplyTap}
      />
    );
  }

  // Draft (own content only)
  if (isOwnUser && media.isDraft) {
    chips.push(
      <Chip
        key="draft"
        qa={{ id: `content-link-draft-${contentId}`, status: 'processing' }}
        label={minified ? undefined : l10n.content.publishDraft}
        iconPath={minified ? '/assets/images/draft.png' : undefined}
        onClick={onDraftTap}
      />
    );
  }

  // Blocked (own content only)
  if (isOwnUser && media.blocked) {
    chips.push(
      <Chip
        key="blocked"
        qa={{ id: `content-link-blocked-${contentId}` }}
        label={minified ? undefined : l10n.content.blockedTapToAppeal}
        iconPath={minified ? '/assets/images/block.png' : undefined}
        onClick={onContentBlockedTap}
      />
    );
  }

  if (chips.length === 0) {
    return null;
  }

  return (
    <div
      className={`flex flex-wrap ${className}`}
      style={{
        gap: `${responsive.gap.lg}px`
      }}
    >
      {chips}
    </div>
  );
};
